// conventions-allow: file-size — pre-existing entity API module already far over budget; PR30 only
// threads an existing systemId value through 3 mutations' params, adding no logic to extract.
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { fetchDatasetsFromDjango } from 'api/dataset/dataset.api'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { setElplan, setElplanInfoStore } from 'store/elplan/elplan'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import { fetchDatasetsFromDbS } from 'utils/dbs/dbs'
import sentry from 'utils/sentry/sentry'
import { formatElplanAlertMessage } from './elplan.alert.helper'
import type { ElplanTranslate } from './elplan.alert.types'
import {
  ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
  ELPLAN_BID_WRITE_MUTATION_KEY,
  ELPLAN_REFRESH_EVENT_QUERY_KEY,
} from './elplan.constants'
import { resolveElplanErrorAlert } from './elplan.errorAlert.helper'
import { getErrorBodyText, getFirstNonFieldError } from './elplan.errorBody.helper'
import type {
  Bid,
  BidToCreate,
  BidTypeName,
  CreateBidData,
  DeleteBidData,
  ElectricityPriceScenario,
  ElplanBidClearLatestDateResponse,
  ElplanBidDayTableBody,
  ElplanBidRequestData,
  ElplanDeleteResponse,
  ElplanInfo,
  ElplanInfoTagForDate,
  PlanValues,
  UpdateBidData,
  UpdateElplanBidsBody,
} from './elplan.types'
import { composeElplanWarningAlert } from './elplan.warningAlert.helper'
import type { ServerErrorResponse } from './types'

export const ELPLAN_BID_MATRIX_REFRESH_EVENT_QUERY_KEY = 'elplan_bid_matrix_refresh_token'
const ELPLAN_BIDS_QUERY_RESOURCE = 'elplan_bids'

export type ScenarioElectricityPriceData = {
  id: number | string
  values: {
    time: ISODateTime
    value: number | undefined
  }[]
  status?: 'error'
  error_msg?: string
}

export type PostScenarioElectricityPriceDataBody = {
  start_time: ISODateTime
  end_time: ISODateTime
  settings: Array<{
    level: string
    name: string
    attribute: string
    value: Array<number | null>
    comment: string
    priority: number
  }>
}

export type Elplan = {
  id: number
  elplan_info: number
  date: ISODateTime
  is_locked: boolean
  elplan_values: PlanValues[]
  locked_at: ISODateTime
  created_at: ISODateTime
  updated_at: ISODateTime
  created_by: ISODateTime
  locked_by: string
  da_results?: PlanValues[]
}

export type CreateElplanBody = {
  elplan_info: number
  date: string
  values: {
    time?: ISODateTime
    volume?: number
  }[]
}

export type UpdateElplanBody = {
  id: number
  values: {
    id: number
    volume?: number
  }[]
}

export const DEFAULT_PRICE_SETTING_PRIORITY = 20
export const SCENARIO_ELECTRICITY_PRICES_QUERY_KEY = 'scenario_electricity_prices'
const SINGLE_SCENARIO_ELECTRICITY_PRICE_QUERY_KEY = 'single_scenario_electricity_price'

// LAGD ELPLAN
export function useElplan(elplanInfoId: number, date: ISODateTime | undefined) {
  const params = {
    elplan_info: elplanInfoId,
    date: date,
  }

  return useQuery({
    queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY, params],

    queryFn: async () => {
      if (typeof elplanInfoId === 'undefined' || elplanInfoId == 0 || typeof date === 'undefined') {
        return null
      }

      const result = await apiClient<Elplan>(`elplan`, { params }).catch(() => null)

      if (result) {
        setElplan(result.id, result.is_locked, true, result.elplan_values, date, result.da_results)
      } else {
        setElplan(0, false, false, [], date, [])
      }

      return result
    },
  })
}

export function useCreateElplanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateElplanBody) => apiClient(`elplan/create`, { method: `POST`, data }),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onSuccess: alertElplanSaved,

    onError: alertElplanWriteError,
  })
}

export function useUpdateElplanMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateElplanBody) => apiClient(`elplan/${data.id}/update`, { method: `PUT`, data }),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onSuccess: alertElplanSaved,

    onError: alertElplanWriteError,
  })
}

function alertElplanSaved(): void {
  alertStore.success(i18n.t('Electricity plan saved'))
}

function alertElplanWriteError(error: ServerErrorResponse): void {
  const bodyText = getErrorBodyText(error).trim()
  alertStore.error(
    getFirstNonFieldError(error) ?? (bodyText || i18n.t('Something went wrong, try again or contact us.'))
  )
}

export function useToggleLockMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number }) => apiClient(`elplan/${data.id}/toggle_lock`, { method: `PATCH` }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onError: (error) => {
      const message = getFirstNonFieldError(error)
      if (message) {
        alertStore.error(message)
      }
    },
  })
}

//BID MANAGEMENT
export function useElplanInfo(systemId: number | undefined, enabled?: boolean) {
  const params = { system_id: systemId }

  return useQuery({
    queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, params],

    queryFn: async (): Promise<ElplanInfo | undefined> => {
      if (systemId === undefined || systemId === null) {
        return
      }

      const result = await apiClient<ElplanInfo>(`elplan_info`, { params })
      if (!result) {
        return
      }

      setElplanInfoStore(
        result.deadline_time,
        result.broker_name,
        result.currency,
        result.bid_types,
        result.excel_file_name,
        result.id,
        result.excel_export_footer_text,
        result.integration,
        result.maxBidVolume,
        result.max_amount_block_and_profile_bids,
        result.only_positive_bids,
        result.scenarios
      )
      return result
    },

    enabled: enabled ?? true,
  })
}

export function useElplanBids(
  elplanInfoId?: number,
  startTime?: ISODateTime,
  endTime?: ISODateTime,
  electricityPriceScenarioId?: number,
  partitionId?: number,
  enabled = true
) {
  const params = {
    elplan_info: elplanInfoId,
    electricity_price_scenario: electricityPriceScenarioId,
    start_time: startTime,
    end_time: endTime,
    ...(partitionId === undefined ? {} : { partition: partitionId }),
  }

  return useQuery({
    queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, { resource: ELPLAN_BIDS_QUERY_RESOURCE, ...params }],

    queryFn: async () => {
      if (elplanInfoId === 0 || typeof startTime === 'undefined' || typeof endTime === 'undefined') {
        return []
      }

      const data = await apiClient<Bid[]>(`elplan_bids`, { params })
      return data
    },

    enabled,
  })
}

export function useElplanInfoTagsForDate(
  elplanInfoId?: number,
  date?: ISODateTime,
  partitionId?: number,
  enabled = true
) {
  const params = {
    elplan_info: elplanInfoId,
    date,
    ...(partitionId === undefined ? {} : { partition: partitionId }),
  }

  return useQuery({
    queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, { resource: 'elplan_info_tags', ...params }],

    queryFn: async (): Promise<ElplanInfoTagForDate[]> => {
      if (!elplanInfoId || !date) {
        return []
      }

      const data = await apiClient<ElplanInfoTagForDate[]>(`elplan_info/tags_for_date`, { params })
      return data
    },

    enabled: enabled && !!elplanInfoId && !!date,
  })
}

export function useElplanBidDayTableSaveMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ElplanBidDayTableBody) => apiClient(`elplan_bid/day_table`, { method: 'POST', data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
    },
  })
}

function alertElplanError(error: ServerErrorResponse, failureKey: string): void {
  const { message, alertKey } = resolveElplanBidErrorAlert(error, failureKey, (key, values) => i18n.t(key, values))
  alertStore.error(message, undefined, alertKey)
}

export function resolveElplanBidErrorAlert(
  error: unknown,
  failureKey: string,
  translate: ElplanTranslate
): { message: string; alertKey?: string } {
  const alert = resolveElplanErrorAlert(error, { operation: undefined, fallback: { key: failureKey } })
  const formatted = formatElplanAlertMessage(alert, translate)
  return { message: alert.alertKey ? formatted : (getFirstNonFieldError(error) ?? formatted), alertKey: alert.alertKey }
}

export function useElplanBidUpdateMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateBidData) => apiClient(`elplan_bid/${data.id}/update`, { method: `PUT`, data }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onError: (error) => alertElplanError(error, 'Something went wrong, try again or contact us.'),
  })
}

export function useElplanBidCreateMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateBidData) => apiClient(`elplan_bid`, { method: `POST`, data }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onError: (error) => alertElplanError(error, 'Failed to create bid'),
  })
}

export function useElplanBidDeleteMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: DeleteBidData) =>
      apiClient<ElplanDeleteResponse>(`elplan_bid/${data.id}/delete`, { method: `DELETE`, data }),

    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
      const warning = composeElplanWarningAlert(response, t)
      if (warning) {
        alertStore.warning(warning.message, undefined, warning.alertKey)
      }
    },

    onError: (error) => {
      const message = getFirstNonFieldError(error)
      if (message) {
        alertStore.error(message)
      }
    },
  })
}

type CreateElplanBidsBody = {
  elplan_info: number
  type: BidTypeName
  electricity_price_scenario?: number
  partition?: number
  bids: BidToCreate[]
}

type ElplanBidsMutationOptions = { suppressAlerts?: boolean }

function refreshElplanBidQueries(queryClient: QueryClient): Promise<void> {
  const bidListRefresh = queryClient.invalidateQueries({
    queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, { resource: ELPLAN_BIDS_QUERY_RESOURCE }],
  })
  void queryClient.invalidateQueries({
    queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
    predicate: ({ queryKey }) => {
      const resource = queryKey[1]
      return (
        typeof resource !== 'object' ||
        resource === null ||
        !('resource' in resource) ||
        resource.resource !== ELPLAN_BIDS_QUERY_RESOURCE
      )
    },
  })
  return bidListRefresh
}

export function useCreateElplanBidsMutation({ suppressAlerts = false }: ElplanBidsMutationOptions = {}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [ELPLAN_BID_WRITE_MUTATION_KEY],
    mutationFn: (data: CreateElplanBidsBody) =>
      apiClient<Bid[] | null>(`elplan_bid/create_elplan_bids`, { method: `POST`, data }),

    onSuccess: async (createdBids: Bid[] | null) => {
      await refreshElplanBidQueries(queryClient)
      if (!suppressAlerts && createdBids && createdBids[0]) {
        const type =
          createdBids[0].type === 'blockbid'
            ? t('block bid')
            : createdBids[0].type === 'spotbid_without_price'
              ? t('spot bid')
              : t('bid')
        alertStore.success(`${t('Successfully created')} ${type}`)
      }
    },

    onError: (error) => {
      if (!suppressAlerts) alertElplanError(error, 'Something went wrong, try again or contact us.')
    },
  })
}

export function useDeleteElplanBidsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient(`elplan_bid/delete_elplan_bid`, { method: `DELETE`, data }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onError: (error) => {
      const message = getFirstNonFieldError(error)
      if (message) {
        alertStore.error(message)
      }
    },
  })
}

export function useUpdateElplanBidsMutation({ suppressAlerts = false }: ElplanBidsMutationOptions = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [ELPLAN_BID_WRITE_MUTATION_KEY],
    mutationFn: (data: UpdateElplanBidsBody) => apiClient(`elplan_bid/update_elplan_bids`, { method: `PUT`, data }),

    onSuccess: () => refreshElplanBidQueries(queryClient),

    onError: (error) => {
      const message = getFirstNonFieldError(error)
      if (message && !suppressAlerts) {
        alertStore.error(message)
      }
    },
  })
}

export function useCopyBidsFromDate() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [ELPLAN_BID_WRITE_MUTATION_KEY],
    mutationFn: (data: {
      elplan_info: number
      from_date: ISODateTime
      to_date: ISODateTime
      type: string
      partition?: number
    }) => apiClient(`elplan_bid/copy_bids_from_date`, { method: `POST`, data }),

    onSuccess: async () => {
      await refreshElplanBidQueries(queryClient)
      alertStore.success(t('Bids copied successfully'))
    },

    onError: (error: ServerErrorResponse, _variables) => {
      if (error.body === 'DstError') {
        alertStore.error(t('Could not copy bids - date is in DST change.'))
      } else if (error.body === 'NoBidsFoundError') {
        alertStore.error(
          t('No bids found for {{chosenDate}} - cannot copy bids', { chosenDate: _variables.from_date as string })
        )
      } else {
        const alert = resolveElplanErrorAlert(error, {
          operation: undefined,
          fallback: { key: 'Something went wrong, try again or contact us.' },
        })
        alertStore.error(formatElplanAlertMessage(alert, t), undefined, alert.alertKey)
        if (alert.alertKey) {
          return
        }

        const errorFingerprintKey =
          error.body === 'DstError' || error.body === 'NoBidsFoundError' ? error.body : 'unexpected_error'

        sentry.captureScopedMessage({
          scope: 'elplan',
          issueType: 'copy_bids_from_date_failure',
          message: getErrorBodyText(error) || error.message,
          extra: {
            body: error.body,
            message: error.message,
            fromDate: _variables.from_date,
            toDate: _variables.to_date,
            elplanInfo: _variables.elplan_info,
            bidType: _variables.type,
          },
          fingerprint: ['elplan', 'copy_bids_from_date_failure', errorFingerprintKey],
        })
      }
    },
  })
}

export function useDeleteElplanBidsForDateMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [ELPLAN_BID_WRITE_MUTATION_KEY],
    mutationFn: (data: { elplan_info: number; date: ISODateTime; type: string; partition?: number }) =>
      apiClient<ElplanDeleteResponse>(`elplan_bid/delete_bids_for_date`, { method: `POST`, data }),

    onSuccess: async (response) => {
      await refreshElplanBidQueries(queryClient)
      alertStore.success(t('Successfully deleted bids'))
      const warning = composeElplanWarningAlert(response, t)
      if (warning) {
        alertStore.warning(warning.message, undefined, warning.alertKey)
      }
    },

    onError: (errors) => {
      const errorMessages = errors as { non_field_errors?: string[] }
      const nonFieldErrors = errorMessages.non_field_errors ?? []
      if (nonFieldErrors) {
        alertStore.error(nonFieldErrors[0])
      }
    },
  })
}

function elplanBidRequestParams(data: ElplanBidRequestData) {
  return { date: data.date, system: data.system, system_id: data.systemId }
}

export function useElplanSendingBidMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ElplanBidRequestData) =>
      apiClient(`elplan_bids/place`, {
        method: `POST`,
        data: { date: data.date, system: data.system },
        params: elplanBidRequestParams(data),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
      alertStore.success(t('Bids sent.'))
    },

    onError: (error: ServerErrorResponse) => {
      const alert = resolveElplanErrorAlert(error, { operation: 'send' })
      alertStore.error(formatElplanAlertMessage(alert, t), undefined, alert.alertKey)
    },
  })
}

/**
 * Withdraws an already-sent Day Ahead day through the integration.
 *
 * A clear also deletes the day's locally-stored bids and records the withdrawal server-side
 * (`ElplanBidClearRecord`), so both the bid-set and the banner-refresh signals are invalidated.
 */
export function useElplanClearBidsMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ElplanBidRequestData) =>
      apiClient(`elplan_bids/clear`, {
        method: `POST`,
        params: elplanBidRequestParams(data),
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_BID_REFRESH_EVENT_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
      alertStore.success(t('Withdrawal sent.'))
    },

    onError: (error: ServerErrorResponse) => {
      const alert = resolveElplanErrorAlert(error, { operation: 'withdraw' })
      alertStore.error(t(alert.key, alert.values), undefined, alert.alertKey)
    },
  })
}

export type PlaceIntradayPositionsPayload = {
  systemId: number
  positions: Array<{
    position_id: string
    tags: Array<{
      tag_id: string
      buy?: Array<{
        time: ISODateTime
        value: number
      }>
      sell?: Array<{
        time: ISODateTime
        value: number
      }>
    }>
  }>
}

export type PlaceIntradayPositionsResponse = {
  message: string
  batch_id?: number
  intraday_integration?: string
  integration_status_code?: number
  integration_response?: unknown
  unmapped_series_ids?: string[]
}

export function usePlaceIntradayPositionsMutation() {
  return useMutation({
    mutationFn: (data: PlaceIntradayPositionsPayload) =>
      apiClient<PlaceIntradayPositionsResponse>(`elplan_bids/intraday/submit`, {
        method: `POST`,
        data,
      }),
  })
}

export function useSentBidsToBroker(
  elplanInfoId: number | undefined,
  startTime: ISODateTime | undefined,
  endTime: ISODateTime | undefined,
  type: string | undefined
) {
  const params = {
    elplan_info: elplanInfoId,
    start_time: startTime,
    end_time: endTime,
    type: type ?? undefined,
  }
  return useQuery({
    queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY, params],

    queryFn: async (): Promise<{ latest_date: ISODateTime } | null> => {
      const data = await apiClient<{ latest_date: ISODateTime }>(`elplan_bids/sent_to_broker_latest_date`, { params })
      return data
    },

    enabled: elplanInfoId !== undefined && elplanInfoId !== 0 && startTime !== undefined && endTime !== undefined,
  })
}

export function useElplanBidClearLatestDate(elplanInfoId: number | undefined, date: ISODateTime | undefined) {
  const { activeSystem } = useAuth()
  const params = { elplan_info: elplanInfoId, date: date ? Datetime.toLocalTime(date, 'onlyDate') : undefined }
  return useQuery({
    queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY, { resource: 'clear_latest_date', system: activeSystem?.id, ...params }],

    queryFn: async (): Promise<ElplanBidClearLatestDateResponse> => {
      const data = await apiClient<ElplanBidClearLatestDateResponse>(`elplan_bids/clear_latest_date`, { params })
      return data
    },

    enabled: activeSystem !== null && elplanInfoId !== undefined && elplanInfoId !== 0 && date !== undefined,
  })
}

export function useElplanSyncResultsMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ElplanBidRequestData) =>
      apiClient<Elplan | null>(`elplan/sync_results`, {
        method: `POST`,
        data: { date: data.date, system: data.system },
        params: elplanBidRequestParams(data),
      }),

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY],
      })
    },

    onSuccess: (result: Elplan | null) => {
      if (!result) {
        alertStore.info(t('No results are available for this day.'))
        return
      }

      alertStore.success(t('Results fetched.'))
      setElplan(result.id, result.is_locked, true, result.elplan_values, result.date)
    },

    onError: (error: ServerErrorResponse) => {
      const alert = resolveElplanErrorAlert(error, { operation: 'fetchResults' })
      alertStore.error(t(alert.key, alert.values), undefined, alert.alertKey)
    },
  })
}

export async function getSpotPriceForecast(
  organization: string | undefined,
  system: string | undefined,
  startTime: ISODateTime,
  endTime: ISODateTime
): Promise<ScenarioElectricityPriceData[]> {
  const returnId = 'el_price_forecast'

  if (!organization || !system) {
    sentry.captureScopedMessage({
      scope: 'elplan',
      issueType: 'missing_spot_price_context',
      message: 'Cannot get spot price forecast - missing organization or system',
      extra: {
        organization,
        system,
        startTime,
        endTime,
      },
    })
    return []
  }
  const datasetInstruction: Partial<DatasetInstruction> = {
    type: 'dbs',
    return_id: returnId,
    filter: {
      start_time: startTime,
      end_time: endTime,
    },
    contract: {
      metadata_filter: {
        include: {
          system: [system],
          abs_service: ['abs_electricity_price'],
          organization: [organization],
          dataset_type: ['forecast'],
        },
        exclude: {},
      },
    },
  }

  const datasets = await fetchDatasetsFromDbS([datasetInstruction], {
    onlyReturnIds: [returnId],
    returnIdRegexFilter: undefined,
  })

  const results = datasets.map((dataset) => {
    return {
      id: dataset.return_id,
      values: dataset.times.map((time, index) => ({
        time: typeof time === 'number' ? (new Date(time).toISOString() as ISODateTime) : time,
        value: dataset.values[index] ?? undefined,
      })),
    }
  })

  return results
}

function getScenarioElectricityPricesQueryKey(
  scenarioPriceConfigs: { scenarioId: number | string; propertyName: string }[],
  systemId: number | undefined,
  startTime: ISODateTime | undefined,
  endTime: ISODateTime | undefined
) {
  return [SCENARIO_ELECTRICITY_PRICES_QUERY_KEY, scenarioPriceConfigs, systemId, startTime, endTime]
}

function getElectricityPriceDatasetInstructions(
  propertyName: string,
  startTime: ISODateTime,
  endTime: ISODateTime,
  systemId: number | undefined,
  digitalTwinName: string | undefined,
  settingPriorities: ElectricityPriceScenario['setting_priority'][] = []
): Partial<DatasetInstruction> {
  return {
    type: 'dt_property_values',
    return_id: propertyName,
    filter: {
      start_time: startTime,
      end_time: endTime,
    },
    contract: {
      sources: [
        {
          return_id: propertyName,
          object_name: digitalTwinName,
          property_name: propertyName,
          setting_level: 'model',
        },
      ],
      system_id: systemId?.toString() ?? '',
      priority_levels:
        settingPriorities.filter((priority): priority is number => priority !== undefined).length > 0
          ? settingPriorities.filter((priority): priority is number => priority !== undefined)
          : [DEFAULT_PRICE_SETTING_PRIORITY],
      use_default_values: false,
    },
  }
}

//Get the current electricity price values for each scenario
export function useGetScenarioElectricityPrices(
  scenarioPriceConfigs: { scenarioId: number | string; propertyName: string }[],
  startTime: ISODateTime | undefined,
  endTime: ISODateTime | undefined,
  settingPriorities: ElectricityPriceScenario['setting_priority'][]
) {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  const queryKey = getScenarioElectricityPricesQueryKey(scenarioPriceConfigs, activeSystem?.id, startTime, endTime)

  return useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      try {
        if (!startTime || !endTime || !scenarioPriceConfigs.length || !activeSystem?.id) {
          return []
        }

        const datasetInstructions: Partial<DatasetInstruction>[] = scenarioPriceConfigs.map((config) =>
          getElectricityPriceDatasetInstructions(
            config.propertyName,
            startTime,
            endTime,
            activeSystem?.id,
            activeSystem?.primary_digital_twin?.name,
            settingPriorities
          )
        )

        const datasets = await fetchDatasetsFromDjango(datasetInstructions, true)

        return datasets
          .filter((dataset) => scenarioPriceConfigs.find((config) => config.propertyName === dataset.return_id))
          .map((dataset) => {
            const matchingConfig = scenarioPriceConfigs.find((config) => config.propertyName === dataset.return_id)

            return {
              id: matchingConfig?.scenarioId,

              values: dataset.times.map((time, index) => ({
                time: time as ISODateTime,
                value: dataset.values[index],
              })),

              ...(dataset.status === 'error'
                ? {
                    status: 'error',
                    error_msg: dataset.error_msg ?? '',
                  }
                : {}),
            }
          })
      } catch (error) {
        // TODO: We may be able to remove this Sentry logging, as react-query already logs errors
        alertStore.error(t('Failed to load electricity price scenario data'))
        sentry.captureException(error as Error, {
          level: 'warning',
          tags: {
            sentry_scope: 'elplan',
            issue_type: 'load_electricity_price_scenario_failure',
          },
          extra: {
            startTime,
            endTime,
            scenarioCount: scenarioPriceConfigs.length,
          },
          fingerprint: ['elplan', 'load_electricity_price_scenario_failure'],
        })
        return []
      }
    },
    enabled: !!startTime && !!endTime && scenarioPriceConfigs.length > 0,
  })
}

function getSingleScenarioElectricityPriceQueryKey(
  scenarioId: number | string,
  propertyName: string,
  startTime: ISODateTime,
  endTime: ISODateTime,
  systemId?: number,
  digitalTwinName?: string,
  settingPriority?: ElectricityPriceScenario['setting_priority']
) {
  return [
    SINGLE_SCENARIO_ELECTRICITY_PRICE_QUERY_KEY,
    scenarioId,
    propertyName,
    startTime,
    endTime,
    systemId,
    digitalTwinName,
    settingPriority,
  ]
}

//Get the electricity price values for a specific scenario
export async function getScenarioElectricityPrice(
  queryClient: QueryClient,
  scenarioId: number | string,
  propertyName: string,
  startTime: ISODateTime,
  endTime: ISODateTime,
  settingPriorities: ElectricityPriceScenario['setting_priority'][] = [],
  systemId?: number,
  digitalTwinName?: string
): Promise<ScenarioElectricityPriceData[]> {
  if (!startTime || !endTime || !systemId || !digitalTwinName) {
    return []
  }

  const queryKey = getSingleScenarioElectricityPriceQueryKey(
    scenarioId,
    propertyName,
    startTime,
    endTime,
    systemId,
    digitalTwinName,
    settingPriorities.length > 0 ? settingPriorities[0] : undefined
  )

  return queryClient.fetchQuery({
    queryKey,
    queryFn: async () => {
      if (!startTime || !endTime || !systemId || !digitalTwinName) {
        return []
      }

      const datasetInstruction = getElectricityPriceDatasetInstructions(
        propertyName,
        startTime,
        endTime,
        systemId,
        digitalTwinName,
        settingPriorities
      )

      const datasets = await fetchDatasetsFromDjango([datasetInstruction], true)

      return datasets
        .filter((dataset) => dataset.return_id === propertyName)
        .map((dataset) => ({
          id: scenarioId,
          values: dataset.times.map((time, index) => ({
            time: time as ISODateTime,
            value: dataset.values[index] ?? undefined,
          })),
          ...(dataset.status === 'error'
            ? {
                status: 'error',
                error_msg: dataset.error_msg ?? '',
              }
            : {}),
        }))
    },
  })
}

export function usePostScenarioElectricityPricesMutation(
  digitalTwinUid: number | undefined,
  systemId: number | undefined,
  startTime: ISODateTime | undefined,
  endTime: ISODateTime | undefined,
  scenarioPriceConfigs: Array<{ scenarioId: number | string; propertyName: string }>
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const params = { overwrite: 1 }
  return useMutation({
    mutationFn: (data: PostScenarioElectricityPriceDataBody) =>
      apiClient(`digital_twin/${digitalTwinUid}/bulk_time_series_settings`, { method: `POST`, data, params }),

    onSuccess: () => {
      const queryKey = getScenarioElectricityPricesQueryKey(scenarioPriceConfigs, systemId, startTime, endTime)
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      })
    },

    onError: () => {
      alertStore.error(t('Failed to update scenario prices'))
    },
  })
}

export type BidMatrixResult = {
  date: string
  electricity_price_scenario: number
  table_contents: {
    columns: string[]
    rows: (string | number)[][]
  }
}
export function useElplanBidMatrix(params: Record<string, any>, queryRefreshKey?: string, refetch?: boolean) {
  return useQuery({
    queryKey: [queryRefreshKey, params],
    queryFn: async (): Promise<BidMatrixResult | null> => {
      const data = await apiClient<BidMatrixResult>(`elplan/get_elplan_bid_matrix`, { params })
      return data
    },
    enabled: refetch ?? true, // Fetch on mount
  })
}
